/**
 * Subtree exclusion: leave part of the live scene out of the document being
 * written (CONTENT_MODEL_SPEC §2.9).
 *
 * The case it exists for is **editor-only content**: a scene folder may hold a
 * nested `editor/` folder whose document is mounted under the session's
 * document root, and the scene's own save must not carry it. Two rules shape
 * the implementation:
 *
 * 1. **It is a writer option, never wire data.** Nothing on either document
 *    says the other exists, so exclusion cannot be a flag on a node or a
 *    behavior — it is a fact the SESSION knows, passed to the one export that
 *    needs it (`exportFolder({ exclude })`).
 * 2. **One cut, at the capture.** Excluding at the record list — before the
 *    asset passes, before the writer — is what makes "the same scene without
 *    that subtree" and "that scene with it excluded" produce the same document
 *    by construction: the excluded records never exist, so their behaviors
 *    never serialize, their `files[]` entries are never collected, and their
 *    `KHR_interactivity` contributions are never assembled. A file another
 *    (non-excluded) node also names is collected from that node, so shared
 *    files stay.
 *
 * Membership is decided on the LIVE objects, by ancestry rather than by the
 * name paths the records carry: names are only sibling-unique, and a record's
 * parent path is rewritten by the subtree writers.
 */
import type { SceneObject } from '../core/SceneObject';
import type { SceneObjectData } from '../scenedata/sceneobjectdata';

/**
 * What `exclude` accepts: a shape that can be iterated MORE THAN ONCE. One
 * export reads the option several times — the record cut, then the legacy
 * GLB-root scan and bake beside it — so a one-shot iterable (a generator, a
 * lazy `.values()`) would be drained by the first reader and look empty to the
 * rest, silently diverging what was excluded from what was warned about.
 */
export type ExcludedSubtrees = readonly SceneObject[] | ReadonlySet<SceneObject>;

/** The export option itself — mixed into every folder/document writer's options. */
export interface SubtreeExclusionOptions {
    /**
     * Subtrees to leave out of the written document: every node in one, its
     * behaviors, its `files[]` entries (unless a node outside the exclusion
     * names the same file) and its `KHR_interactivity` contributions are
     * omitted, as if the objects were not in the scene at all.
     *
     * Excluding the document root is an error — it would write an empty
     * capture — and so is any exclusion that covers every root of it.
     *
     * Not persisted and not on the wire (CONTENT_MODEL_SPEC §2.9): the caller
     * is stating something about THIS save.
     */
    exclude?: ExcludedSubtrees;
}

/** Whether `object` is one of `excluded` or sits under one. */
function isExcluded(object: SceneObject, excluded: ReadonlySet<SceneObject>): boolean {
    for (let node: SceneObject | null = object; node; node = node.getParent()) {
        if (excluded.has(node)) return true;
    }
    return false;
}

/**
 * The objects of `scope` the exclusion leaves in the capture — for the passes
 * that walk the LIVE scene beside the records (the legacy GLB-root scan and
 * bake). They must see the same objects the document does, or they warn about
 * and bake models the document never names.
 */
export function withoutExcluded<T extends SceneObject>(
    scope: readonly T[],
    exclude: ExcludedSubtrees | undefined,
): T[] {
    const excluded = new Set(exclude ?? []);
    if (excluded.size === 0) return [...scope];
    return scope.filter(object => !isExcluded(object, excluded));
}

/**
 * The records whose live object has no parent INSIDE the capture: the roots of
 * whatever was captured.
 *
 * Asked on the live objects rather than on `transform.parent`, because what a
 * root looks like in the records differs per capture — a document capture
 * leaves its root's parent unset, the flat `.zip` list leaves several unset,
 * and {@link TraceScene.getExportData} writes an absolute path on every record
 * including the top-level ones. Identity answers the same way for all three.
 *
 * A record with no live object is the SYNTHESIZED root (SCENE_ROOT_SPEC §4); it
 * is a root, and it can never be excluded — which is why the "excluded
 * everything" guard in {@link applySubtreeExclusion} does not count it.
 */
function captureRoots(
    liveObjects: readonly (SceneObject | null)[],
): Set<number> {
    const captured = new Set(liveObjects.filter((o): o is SceneObject => o !== null));
    const roots = new Set<number>();
    for (let i = 0; i < liveObjects.length; i++) {
        const live = liveObjects[i];
        if (!live) { roots.add(i); continue; }
        const parent = live.getParent();
        if (!parent || !captured.has(parent)) roots.add(i);
    }
    return roots;
}

/**
 * Drop every record captured from an excluded subtree.
 *
 * `data` and `liveObjects` are the parallel arrays every capture produces (a
 * synthesized root pairs with `null`). Returns new arrays; the inputs are not
 * mutated.
 *
 * @throws when the exclusion covers every root of the capture — which is what
 *   excluding the document root does.
 */
export function applySubtreeExclusion(
    data: readonly SceneObjectData[],
    liveObjects: readonly (SceneObject | null)[],
    exclude: ExcludedSubtrees | undefined,
): { data: SceneObjectData[]; liveObjects: (SceneObject | null)[] } {
    const excluded = new Set(exclude ?? []);
    if (excluded.size === 0) {
        return { data: [...data], liveObjects: [...liveObjects] };
    }

    const roots = captureRoots(liveObjects);
    // The guard below is decided on the LIVE roots alone: a synthesized root
    // (paired with `null`) is never excluded, so counting it would let an
    // exclusion that covers every real root through as a document holding
    // one empty group node.
    let liveRoots = 0;
    let keptLiveRoots = 0;

    const keptData: SceneObjectData[] = [];
    const keptLive: (SceneObject | null)[] = [];
    for (let i = 0; i < data.length; i++) {
        const live = liveObjects[i] ?? null;
        const isLiveRoot = live !== null && roots.has(i);
        if (isLiveRoot) liveRoots++;
        if (live && isExcluded(live, excluded)) continue;
        if (isLiveRoot) keptLiveRoots++;
        keptData.push(data[i]);
        keptLive.push(live);
    }

    // Losing every root means the exclusion covered the whole capture — the
    // document root itself, or everything under it.
    if (liveRoots > 0 && keptLiveRoots === 0) {
        throw new Error(
            'export exclude: the excluded subtree(s) cover every root of this capture — '
            + 'excluding the document root would write an empty document. Exclude the '
            + 'editor subtree, not the root it is mounted under (CONTENT_MODEL_SPEC §2.9).',
        );
    }

    return { data: keptData, liveObjects: keptLive };
}
